import { toast } from 'react-toastify'
import { postCPDataWithoutParams } from 'src/clinic/services'
import { DISPATCH_EVENT_KEYS } from 'src/consumer/constants'
import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { broadcastCheckInUpdate, broadcastCheckInRemoval } from './checkInSyncService'
import { getClientDeviceInfo } from 'src/consumer/services/deviceInfoCollector'

/**
 * Clinic Assistant Check-in Service
 * Implements the check-in API for clinic assistants as per the contract:
 * POST /api/secure/clinic-assistant/emr/usage/log/events
 */

/**
 * Log a clinic assistant check-in event
 * @param {Object} params
 * @param {string} params.logEventId - Event ID: 'log-check-in', 'log-check-out', 'log-screen-idle'
 * @param {string} params.chamberRoomId - The chamber room ID
 * @param {string} [params.noteForLogging] - Optional note for logging
 * @returns {Promise<Object>} API response
 */
export async function logClinicAssistantEvent({ logEventId, chamberRoomId, noteForLogging }) {
  if (!logEventId) {
    const error = { status: 400, data: { code: 41923, message: 'logEventId is required' } }
    toast.error('Missing required parameters')
    return error
  }

  // Default chamberRoomId to "reception" if not available or defined
  const finalChamberRoomId = chamberRoomId || 'reception'
  
  // Collect device info for logging
  const clientDeviceInfo = getClientDeviceInfo()

  try {
    const response = await postCPDataWithoutParams(
      '/api/secure/clinic-assistant/emr/usage/log/events',
      {
        logEventId,
        chamberRoomId: finalChamberRoomId,
        apiParam: 'ca-usage-log',
        ...(noteForLogging && { noteForLogging }),
        clientDeviceInfo,
      }
    )

    if (response?.status === 200) {
      if (isCommonResponseSuccessfulV2(response)) {
        // Dispatch appropriate event based on logEventId
        if (logEventId === 'log-check-in') {
          window.dispatchEvent(
            new CustomEvent(DISPATCH_EVENT_KEYS.userCheckedIn, {
              detail: {
                roomData: response?.data?.data?.['chamber-room'],
                usageLog: response?.data?.data?.['ca-usage-log'],
              },
            })
          )
        } else if (logEventId === 'log-check-out') {
          window.dispatchEvent(
            new CustomEvent(DISPATCH_EVENT_KEYS.userCheckedOut, {
              detail: {},
            })
          )
        }
      } else {
        const errorMessage = response?.data?.message || 'Operation failed'
        toast.error(errorMessage)
      }
    } else {
      const errorMessage = response?.data?.message || 'Request failed'
      toast.error(errorMessage)
    }

    return response
  } catch (error) {
    console.error('Error logging clinic assistant event:', error)
    toast.error(error?.message || 'An unexpected error occurred')
    return {
      status: error?.response?.status || 500,
      data: {
        code: error?.response?.data?.code || 500,
        message: error?.response?.data?.message || error?.message || 'An unexpected error occurred',
      },
    }
  }
}

/**
 * Check in to a chamber room
 * @param {string} [chamberRoomId] - The chamber room ID (defaults to "reception" if not provided)
 * @param {string} [noteForLogging] - Optional note for logging
 * @returns {Promise<Object>} API response with ca-usage-log
 */
export async function checkInClinicAssistant(chamberRoomId, noteForLogging) {
  const response = await logClinicAssistantEvent({
    logEventId: 'log-check-in',
    chamberRoomId: chamberRoomId || 'reception',
    noteForLogging,
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const caUsageLog = response?.data?.data?.['ca-usage-log']
    const chamberRoom = response?.data?.data?.['chamber-room']

    if (caUsageLog) {
      const checkInData = {
        ...caUsageLog,
        roomName: chamberRoom?.title,
        lastValidated: new Date().toISOString(),
      }
      localStorage.setItem('checkedInDetails', JSON.stringify(checkInData))
      // Broadcast update to other tabs
      broadcastCheckInUpdate(checkInData, chamberRoom)
    }

    if (chamberRoom) {
      localStorage.setItem('peekedInRoomDetails', JSON.stringify(chamberRoom))
    }

    toast.success('Checked in successfully')
  }

  return response
}

/**
 * Check out from a chamber room
 * @param {string} [chamberRoomId] - The chamber room ID (defaults to "reception" if not provided)
 * @param {string} [noteForLogging] - Optional note for logging
 * @returns {Promise<Object>} API response
 */
export async function checkOutClinicAssistant(chamberRoomId, noteForLogging) {
  const response = await logClinicAssistantEvent({
    logEventId: 'log-check-out',
    chamberRoomId: chamberRoomId || 'reception',
    noteForLogging,
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    // Clear check-in details from localStorage
    localStorage.removeItem('checkedInDetails')
    localStorage.removeItem('peekedInRoomDetails')
    broadcastCheckInRemoval()
    toast.success('Checked out successfully')
  }

  return response
}

/**
 * Log screen idle state
 * @param {string} [chamberRoomId] - The chamber room ID (defaults to "reception" if not provided)
 * @param {string} [noteForLogging] - Optional note for logging
 * @returns {Promise<Object>} API response
 */
export async function logScreenIdleClinicAssistant(chamberRoomId, noteForLogging) {
  const response = await logClinicAssistantEvent({
    logEventId: 'log-screen-idle',
    chamberRoomId: chamberRoomId || 'reception',
    noteForLogging: noteForLogging || 'Screen idle state recorded',
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const timestamp = new Date().toLocaleTimeString()
    toast.info(`Idle state recorded at ${timestamp}`, { autoClose: 3000 })
  }

  return response
}

/**
 * Refresh check-in (currently returns 514 error as per API docs, but implemented for future support)
 * @param {string} [chamberRoomId] - The chamber room ID (defaults to "reception" if not provided)
 * @param {string} [noteForLogging] - Optional note for logging
 * @returns {Promise<Object>} API response
 */
export async function refreshCheckInClinicAssistant(chamberRoomId, noteForLogging) {
  const response = await logClinicAssistantEvent({
    logEventId: 'log-check-in-refresh',
    chamberRoomId: chamberRoomId || 'reception',
    noteForLogging,
  })

  // Note: Currently returns 514 error as per API docs, but this function is ready for when backend support is added
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const caUsageLog = response?.data?.data?.['ca-usage-log']
    const chamberRoom = response?.data?.data?.['chamber-room']

    if (caUsageLog) {
      const checkInData = {
        ...caUsageLog,
        roomName: chamberRoom?.title,
        lastValidated: new Date().toISOString(),
      }
      localStorage.setItem('checkedInDetails', JSON.stringify(checkInData))
      // Broadcast update to other tabs
      broadcastCheckInUpdate(checkInData, chamberRoom)
    }

    if (chamberRoom) {
      localStorage.setItem('peekedInRoomDetails', JSON.stringify(chamberRoom))
    }

    toast.success('Check-in refreshed successfully')
  }

  return response
}

